import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { authService, RegisterInput } from '@/services/auth';
import { getApiError } from '@/services/api';
import { AlertCircle } from 'lucide-react';

type RegisterForm = RegisterInput & { confirmPassword: string };

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password');

  const registerMutation = useMutation({
    mutationFn: authService.register,
    onSuccess: () => {
      navigate('/login', { state: { registered: true } });
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const onSubmit = (data: RegisterForm) => {
    setError(null);
    const { confirmPassword, ...registerData } = data;
    registerMutation.mutate(registerData);
  };

  const fieldClass = (hasError: boolean) => (hasError ? 'input-error' : 'input');
  const requiredMsg = t('auth.fieldRequired');

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{t('auth.register')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('auth.registerSubtitle')}</p>
      </div>

      {error && (
        <div className="alert-danger mb-6" role="alert">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="label">
              {t('auth.firstName')}
            </label>
            <input
              type="text"
              id="firstName"
              autoComplete="given-name"
              className={fieldClass(!!errors.firstName)}
              {...register('firstName', { required: requiredMsg, minLength: { value: 2, message: requiredMsg } })}
            />
            {errors.firstName && <p className="field-error">{errors.firstName.message}</p>}
          </div>

          <div>
            <label htmlFor="lastName" className="label">
              {t('auth.lastName')}
            </label>
            <input
              type="text"
              id="lastName"
              autoComplete="family-name"
              className={fieldClass(!!errors.lastName)}
              {...register('lastName', { required: requiredMsg, minLength: { value: 2, message: requiredMsg } })}
            />
            {errors.lastName && <p className="field-error">{errors.lastName.message}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="label">
            {t('auth.email')}
          </label>
          <input
            type="email"
            id="email"
            autoComplete="email"
            dir="ltr"
            className={fieldClass(!!errors.email)}
            placeholder="email@example.com"
            {...register('email', {
              required: t('auth.errors.emailRequired'),
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('validation.emailInvalid') },
            })}
          />
          {errors.email && <p className="field-error">{errors.email.message}</p>}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="role" className="label">
              {t('auth.role')}
            </label>
            <select id="role" className={fieldClass(!!errors.role)} {...register('role', { required: requiredMsg })}>
              <option value="">{t('common.select')}</option>
              <option value="DOCTOR">{t('roles.doctor')}</option>
              <option value="PHARMACIST">{t('roles.pharmacist')}</option>
              <option value="PATIENT">{t('roles.patient')}</option>
            </select>
            {errors.role && <p className="field-error">{errors.role.message}</p>}
          </div>

          <div>
            <label htmlFor="licenseNumber" className="label">
              {t('auth.licenseNumber')} <span className="font-normal text-gray-400">({t('common.optional')})</span>
            </label>
            <input type="text" id="licenseNumber" className="input" {...register('licenseNumber')} />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="label">
            {t('auth.password')}
          </label>
          <input
            type="password"
            id="password"
            autoComplete="new-password"
            className={fieldClass(!!errors.password)}
            placeholder="••••••••••••"
            {...register('password', {
              required: t('auth.errors.passwordRequired'),
              minLength: {
                value: 12,
                message: t('validation.passwordTooShort'),
              },
            })}
          />
          {errors.password ? (
            <p className="field-error">{errors.password.message}</p>
          ) : (
            <p className="field-hint">{t('validation.passwordTooShort')}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="label">
            {t('auth.confirmPassword')}
          </label>
          <input
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            className={fieldClass(!!errors.confirmPassword)}
            placeholder="••••••••••••"
            {...register('confirmPassword', {
              required: t('auth.errors.passwordRequired'),
              validate: (value) => value === password || t('auth.errors.passwordMismatch'),
            })}
          />
          {errors.confirmPassword && <p className="field-error">{errors.confirmPassword.message}</p>}
        </div>

        <button type="submit" disabled={registerMutation.isPending} className="btn-primary btn-lg mt-2 w-full">
          {registerMutation.isPending ? t('common.loading') : t('auth.register')}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-gray-500">
        {t('auth.haveAccount')}{' '}
        <Link to="/login" className="link">
          {t('auth.login')}
        </Link>
      </p>
    </div>
  );
}
